import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FileText, Table, List, Plus, Edit2, Trash2, Save, X, Eye, 
  CheckCircle, Clock, Archive, ChevronDown, ChevronRight, Search,
  GripVertical, ToggleLeft, ToggleRight, RefreshCw, AlertCircle,
  Upload, Download, History, RotateCcw, Filter, CheckSquare, Square,
  XCircle, AlertTriangle, Layers, FileUp, FileDown, MoreVertical,
  Undo2, BookOpen, Activity
} from 'lucide-react';
import './BlueprintAdmin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    published: { icon: CheckCircle, color: 'green', label: 'Published' },
    draft: { icon: Clock, color: 'yellow', label: 'Draft' },
    in_review: { icon: AlertCircle, color: 'blue', label: 'In Review' },
    approved: { icon: CheckSquare, color: 'emerald', label: 'Approved' },
    archived: { icon: Archive, color: 'gray', label: 'Archived' }
  };
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;
  
  return (
    <span className={`bp-status-badge bp-status-${config.color}`} data-testid={`status-${status}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Confidence badge
const ConfidenceBadge = ({ level }) => {
  const colors = { high: 'green', medium: 'yellow', low: 'orange', unverified: 'gray' };
  return (
    <span className={`bp-confidence bp-conf-${colors[level] || 'gray'}`}>
      {level || 'unverified'}
    </span>
  );
};

const BlueprintAdmin = () => {
  // Core state
  const [blueprints, setBlueprints] = useState([]);
  const [selectedBlueprint, setSelectedBlueprint] = useState(null);
  const [activeTab, setActiveTab] = useState('tables');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Content state
  const [sections, setSections] = useState([]);
  const [tables, setTables] = useState([]);
  const [lists, setLists] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableRows, setTableRows] = useState([]);
  
  // V2 Features state
  const [versions, setVersions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingRow, setEditingRow] = useState(null);
  const [editRowData, setEditRowData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [notification, setNotification] = useState(null);
  const fileInputRef = useRef(null);

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Fetch functions
  const fetchBlueprints = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blueprints`);
      const data = await res.json();
      setBlueprints(data);
      if (data.length > 0 && !selectedBlueprint) {
        setSelectedBlueprint(data[0]);
      }
    } catch (err) {
      console.error('Error fetching blueprints:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blueprints/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchBlueprintContent = useCallback(async (slug) => {
    try {
      const [sectionsRes, tablesRes, listsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/blueprints/${slug}/sections`),
        fetch(`${API_URL}/api/admin/blueprints/${slug}/tables`),
        fetch(`${API_URL}/api/admin/blueprints/${slug}/lists`)
      ]);
      
      setSections(await sectionsRes.json());
      setTables(await tablesRes.json());
      setLists(await listsRes.json());
    } catch (err) {
      console.error('Error fetching blueprint content:', err);
    }
  }, []);

  const fetchVersions = async (slug) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blueprints/${slug}/versions`);
      const data = await res.json();
      setVersions(data);
    } catch (err) {
      console.error('Error fetching versions:', err);
    }
  };

  const fetchAuditLog = async (slug) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blueprints/${slug}/audit-log?limit=100`);
      const data = await res.json();
      setAuditLog(data);
    } catch (err) {
      console.error('Error fetching audit log:', err);
    }
  };

  const fetchTableRows = async (tableId) => {
    try {
      const url = new URL(`${API_URL}/api/admin/tables/${tableId}/rows`);
      if (showArchived) url.searchParams.set('include_archived', 'true');
      if (statusFilter !== 'all') url.searchParams.set('status', statusFilter);
      
      const res = await fetch(url);
      const data = await res.json();
      setTableRows(data);
      setSelectedRows(new Set());
    } catch (err) {
      console.error('Error fetching rows:', err);
    }
  };

  useEffect(() => {
    fetchBlueprints();
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedBlueprint) {
      fetchBlueprintContent(selectedBlueprint.slug);
      fetchVersions(selectedBlueprint.slug);
      fetchAuditLog(selectedBlueprint.slug);
    }
  }, [selectedBlueprint, fetchBlueprintContent]);

  useEffect(() => {
    if (selectedTable) {
      fetchTableRows(selectedTable.id);
    }
  }, [selectedTable, showArchived, statusFilter]);

  // Row CRUD Operations
  const updateRow = async (rowId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/table-rows/${rowId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        fetchTableRows(selectedTable.id);
        setEditingRow(null);
        setEditRowData({});
        showNotification('Row updated successfully');
      }
    } catch (err) {
      console.error('Error updating row:', err);
      showNotification('Failed to update row', 'error');
    }
  };

  const addNewRow = async () => {
    if (!selectedTable) return;
    try {
      const emptyRowData = {};
      selectedTable.columns.forEach(col => {
        emptyRowData[col.key] = '';
      });
      
      const res = await fetch(`${API_URL}/api/admin/tables/${selectedTable.id}/rows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table_id: selectedTable.id,
          page_slug: selectedBlueprint.slug,
          row_data: emptyRowData,
          status: 'draft'
        })
      });
      if (res.ok) {
        fetchTableRows(selectedTable.id);
        showNotification('New row added');
      }
    } catch (err) {
      console.error('Error adding row:', err);
      showNotification('Failed to add row', 'error');
    }
  };

  const deleteRow = async (rowId, hardDelete = false) => {
    if (!window.confirm(hardDelete ? 'Permanently delete this row?' : 'Archive this row?')) return;
    try {
      const url = new URL(`${API_URL}/api/admin/table-rows/${rowId}`);
      if (hardDelete) url.searchParams.set('hard_delete', 'true');
      
      await fetch(url, { method: 'DELETE' });
      fetchTableRows(selectedTable.id);
      showNotification(hardDelete ? 'Row deleted' : 'Row archived');
    } catch (err) {
      console.error('Error deleting row:', err);
    }
  };

  const restoreRow = async (rowId) => {
    try {
      await fetch(`${API_URL}/api/admin/table-rows/${rowId}/restore`, { method: 'POST' });
      fetchTableRows(selectedTable.id);
      showNotification('Row restored');
    } catch (err) {
      console.error('Error restoring row:', err);
    }
  };

  // Bulk Operations
  const handleSelectAll = () => {
    if (selectedRows.size === filteredRows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredRows.map(r => r.id)));
    }
  };

  const handleSelectRow = (rowId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    setSelectedRows(newSelected);
  };

  const bulkAction = async (action) => {
    if (selectedRows.size === 0) return;
    
    const endpoint = {
      publish: 'bulk-publish',
      unpublish: 'bulk-unpublish',
      archive: 'bulk-archive',
      delete: 'bulk-delete'
    }[action];
    
    if (action === 'delete' && !window.confirm(`Permanently delete ${selectedRows.size} rows?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/tables/${selectedTable.id}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ row_ids: Array.from(selectedRows) })
      });
      const data = await res.json();
      fetchTableRows(selectedTable.id);
      setSelectedRows(new Set());
      showNotification(data.message);
    } catch (err) {
      console.error('Bulk action error:', err);
      showNotification('Bulk action failed', 'error');
    }
  };

  // CSV Operations
  const handleCSVFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/tables/${selectedTable.id}/import-csv/preview`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      setCsvPreview({ ...data, file });
      setShowCSVModal(true);
    } catch (err) {
      console.error('CSV preview error:', err);
      showNotification('Failed to preview CSV', 'error');
    }
  };

  const executeCSVImport = async (mode) => {
    if (!csvPreview?.file) return;
    
    const formData = new FormData();
    formData.append('file', csvPreview.file);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/tables/${selectedTable.id}/import-csv?mode=${mode}`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      setShowCSVModal(false);
      setCsvPreview(null);
      fetchTableRows(selectedTable.id);
      showNotification(`Imported ${data.imported_count} rows, updated ${data.updated_count} rows`);
    } catch (err) {
      console.error('CSV import error:', err);
      showNotification('CSV import failed', 'error');
    }
  };

  const exportCSV = async () => {
    if (!selectedTable) return;
    try {
      const url = new URL(`${API_URL}/api/admin/tables/${selectedTable.id}/export-csv`);
      if (showArchived) url.searchParams.set('include_archived', 'true');
      if (statusFilter !== 'all') url.searchParams.set('status', statusFilter);
      
      window.open(url.toString(), '_blank');
      showNotification('CSV export started');
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  // Version & Rollback
  const rollbackToVersion = async (versionId) => {
    if (!window.confirm('Rollback to this version? Current changes will be replaced.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/blueprints/${selectedBlueprint.slug}/rollback/${versionId}`, {
        method: 'POST'
      });
      const data = await res.json();
      fetchBlueprintContent(selectedBlueprint.slug);
      fetchVersions(selectedBlueprint.slug);
      if (selectedTable) {
        // Table ID may change after rollback, so reload tables
        setSelectedTable(null);
        setTableRows([]);
      }
      setShowVersionHistory(false);
      showNotification(data.message);
    } catch (err) {
      console.error('Rollback error:', err);
      showNotification('Rollback failed', 'error');
    }
  };

  // Publish Operations
  const publishAll = async () => {
    if (!window.confirm('Publish all content for this blueprint?')) return;
    try {
      await fetch(`${API_URL}/api/admin/blueprints/${selectedBlueprint.slug}/publish-all`, {
        method: 'POST'
      });
      fetchBlueprintContent(selectedBlueprint.slug);
      fetchVersions(selectedBlueprint.slug);
      if (selectedTable) fetchTableRows(selectedTable.id);
      showNotification('All content published successfully!');
    } catch (err) {
      console.error('Error publishing:', err);
      showNotification('Publish failed', 'error');
    }
  };

  // Filter rows
  const filteredRows = tableRows.filter(row => {
    if (!searchTerm) return true;
    return Object.values(row.row_data || {}).some(val => 
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="bp-admin" data-testid="blueprint-admin-loading">
        <div className="bp-loading">
          <RefreshCw className="animate-spin" size={24} />
          <span>Loading Blueprint CMS...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bp-admin" data-testid="blueprint-admin">
      {/* Notification Toast */}
      {notification && (
        <div className={`bp-toast bp-toast-${notification.type}`} data-testid="notification">
          {notification.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="bp-header">
        <div className="bp-header-left">
          <h1 className="bp-title">Blueprint CMS</h1>
          <p className="bp-subtitle">Manual-first content management system</p>
        </div>
        <div className="bp-header-right">
          {stats && (
            <div className="bp-stats-mini">
              <span data-testid="stat-pages"><FileText size={14} /> {stats.total_pages} Pages</span>
              <span data-testid="stat-tables"><Table size={14} /> {stats.total_tables} Tables</span>
              <span data-testid="stat-rows"><Layers size={14} /> {stats.total_rows} Rows</span>
              <span data-testid="stat-published" className="bp-stat-published">
                <CheckCircle size={14} /> {stats.published_pages} Published
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Blueprint Selector & Actions */}
      <div className="bp-selector">
        <div className="bp-selector-left">
          <label>Blueprint:</label>
          <select 
            value={selectedBlueprint?.slug || ''} 
            onChange={(e) => {
              const bp = blueprints.find(b => b.slug === e.target.value);
              setSelectedBlueprint(bp);
              setSelectedTable(null);
              setTableRows([]);
            }}
            data-testid="blueprint-selector"
          >
            {blueprints.map(bp => (
              <option key={bp.id} value={bp.slug}>{bp.title}</option>
            ))}
          </select>
          {selectedBlueprint && (
            <StatusBadge status={selectedBlueprint.status} />
          )}
        </div>
        
        {selectedBlueprint && (
          <div className="bp-actions">
            <button 
              className="bp-btn bp-btn-ghost"
              onClick={() => setShowAuditLog(true)}
              data-testid="audit-log-btn"
            >
              <Activity size={16} /> Audit Log
            </button>
            <button 
              className="bp-btn bp-btn-ghost"
              onClick={() => setShowVersionHistory(true)}
              data-testid="version-history-btn"
            >
              <History size={16} /> History ({versions.length})
            </button>
            <button 
              className="bp-btn bp-btn-secondary" 
              onClick={() => window.open(`/blueprints/${selectedBlueprint.slug.replace('-blueprint', '')}`, '_blank')}
              data-testid="preview-btn"
            >
              <Eye size={16} /> Preview
            </button>
            <button 
              className="bp-btn bp-btn-primary" 
              onClick={publishAll}
              data-testid="publish-all-btn"
            >
              <CheckCircle size={16} /> Publish All
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bp-tabs">
        <button 
          className={`bp-tab ${activeTab === 'tables' ? 'active' : ''}`}
          onClick={() => setActiveTab('tables')}
          data-testid="tab-tables"
        >
          <Table size={16} /> Tables ({tables.length})
        </button>
        <button 
          className={`bp-tab ${activeTab === 'sections' ? 'active' : ''}`}
          onClick={() => setActiveTab('sections')}
          data-testid="tab-sections"
        >
          <FileText size={16} /> Sections ({sections.length})
        </button>
        <button 
          className={`bp-tab ${activeTab === 'lists' ? 'active' : ''}`}
          onClick={() => setActiveTab('lists')}
          data-testid="tab-lists"
        >
          <List size={16} /> Lists ({lists.length})
        </button>
      </div>

      {/* Content Area */}
      <div className="bp-content">
        {/* TABLES TAB */}
        {activeTab === 'tables' && (
          <div className="bp-tables-view">
            {/* Table List Sidebar */}
            <div className="bp-table-list">
              <div className="bp-table-list-header">
                <h3>Structured Tables</h3>
              </div>
              {tables.map(table => (
                <div 
                  key={table.id} 
                  className={`bp-table-item ${selectedTable?.id === table.id ? 'active' : ''}`}
                  onClick={() => setSelectedTable(table)}
                  data-testid={`table-${table.section_key}`}
                >
                  <div className="bp-table-info">
                    <h4>{table.title}</h4>
                    <div className="bp-table-meta">
                      <span className="bp-meta-stat">{table.row_count} total</span>
                      <span className="bp-meta-published">{table.published_row_count} published</span>
                      <span className="bp-meta-draft">{table.draft_row_count} draft</span>
                      {table.archived_row_count > 0 && (
                        <span className="bp-meta-archived">{table.archived_row_count} archived</span>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={table.status} />
                </div>
              ))}
            </div>

            {/* Table Row Manager */}
            {selectedTable ? (
              <div className="bp-table-editor">
                {/* Table Header with Actions */}
                <div className="bp-table-header">
                  <div className="bp-table-title-row">
                    <h3>{selectedTable.title}</h3>
                    <span className="bp-table-desc">{selectedTable.description}</span>
                  </div>
                  
                  {/* Toolbar */}
                  <div className="bp-toolbar">
                    <div className="bp-toolbar-left">
                      {/* Search */}
                      <div className="bp-search">
                        <Search size={16} />
                        <input 
                          type="text" 
                          placeholder="Search rows..." 
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          data-testid="search-input"
                        />
                      </div>
                      
                      {/* Filters */}
                      <select 
                        className="bp-filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        data-testid="status-filter"
                      >
                        <option value="all">All Status</option>
                        <option value="published">Published</option>
                        <option value="draft">Draft</option>
                        <option value="in_review">In Review</option>
                        <option value="approved">Approved</option>
                      </select>
                      
                      <label className="bp-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={showArchived}
                          onChange={(e) => setShowArchived(e.target.checked)}
                          data-testid="show-archived"
                        />
                        Show Archived
                      </label>
                    </div>
                    
                    <div className="bp-toolbar-right">
                      {/* Bulk Actions */}
                      {selectedRows.size > 0 && (
                        <div className="bp-bulk-actions">
                          <span className="bp-selected-count">{selectedRows.size} selected</span>
                          <button 
                            className="bp-btn bp-btn-sm bp-btn-success"
                            onClick={() => bulkAction('publish')}
                            data-testid="bulk-publish"
                          >
                            <CheckCircle size={14} /> Publish
                          </button>
                          <button 
                            className="bp-btn bp-btn-sm bp-btn-warning"
                            onClick={() => bulkAction('unpublish')}
                            data-testid="bulk-unpublish"
                          >
                            <Clock size={14} /> Unpublish
                          </button>
                          <button 
                            className="bp-btn bp-btn-sm bp-btn-secondary"
                            onClick={() => bulkAction('archive')}
                            data-testid="bulk-archive"
                          >
                            <Archive size={14} /> Archive
                          </button>
                          <button 
                            className="bp-btn bp-btn-sm bp-btn-danger"
                            onClick={() => bulkAction('delete')}
                            data-testid="bulk-delete"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      )}
                      
                      {/* CSV Actions */}
                      <div className="bp-csv-actions">
                        <input 
                          type="file" 
                          accept=".csv"
                          ref={fileInputRef}
                          onChange={handleCSVFileSelect}
                          style={{ display: 'none' }}
                        />
                        <button 
                          className="bp-btn bp-btn-sm"
                          onClick={() => fileInputRef.current?.click()}
                          data-testid="import-csv"
                        >
                          <FileUp size={14} /> Import CSV
                        </button>
                        <button 
                          className="bp-btn bp-btn-sm"
                          onClick={exportCSV}
                          data-testid="export-csv"
                        >
                          <FileDown size={14} /> Export CSV
                        </button>
                      </div>
                      
                      <button 
                        className="bp-btn bp-btn-sm bp-btn-primary"
                        onClick={addNewRow}
                        data-testid="add-row"
                      >
                        <Plus size={14} /> Add Row
                      </button>
                      <button 
                        className="bp-btn bp-btn-sm bp-btn-ghost"
                        onClick={() => fetchTableRows(selectedTable.id)}
                        data-testid="refresh-rows"
                      >
                        <RefreshCw size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Data Table */}
                <div className="bp-table-wrap">
                  <table className="bp-data-table" data-testid="data-table">
                    <thead>
                      <tr>
                        <th className="bp-col-select">
                          <button 
                            className="bp-select-all"
                            onClick={handleSelectAll}
                            data-testid="select-all"
                          >
                            {selectedRows.size === filteredRows.length && filteredRows.length > 0 
                              ? <CheckSquare size={16} /> 
                              : <Square size={16} />
                            }
                          </button>
                        </th>
                        <th className="bp-col-drag"></th>
                        {selectedTable.columns?.map(col => (
                          <th key={col.key}>{col.label}</th>
                        ))}
                        <th className="bp-col-confidence">Confidence</th>
                        <th className="bp-col-status">Status</th>
                        <th className="bp-col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={selectedTable.columns?.length + 4} className="bp-empty-state">
                            <FileText size={32} />
                            <p>No rows found</p>
                            <button className="bp-btn bp-btn-primary" onClick={addNewRow}>
                              <Plus size={14} /> Add First Row
                            </button>
                          </td>
                        </tr>
                      ) : (
                        filteredRows.map((row) => (
                          <tr 
                            key={row.id} 
                            className={`
                              ${row.featured ? 'bp-row-featured' : ''} 
                              ${row.is_archived ? 'bp-row-archived' : ''}
                              ${selectedRows.has(row.id) ? 'bp-row-selected' : ''}
                            `}
                            data-testid={`row-${row.id}`}
                          >
                            <td className="bp-col-select">
                              <button 
                                className="bp-row-select"
                                onClick={() => handleSelectRow(row.id)}
                              >
                                {selectedRows.has(row.id) 
                                  ? <CheckSquare size={16} /> 
                                  : <Square size={16} />
                                }
                              </button>
                            </td>
                            <td className="bp-col-drag">
                              <GripVertical size={14} className="bp-drag-handle" />
                            </td>
                            {selectedTable.columns?.map(col => (
                              <td key={col.key}>
                                {editingRow === row.id ? (
                                  <input 
                                    type="text"
                                    className="bp-cell-input"
                                    value={editRowData[col.key] ?? row.row_data?.[col.key] ?? ''}
                                    onChange={(e) => setEditRowData({
                                      ...editRowData,
                                      [col.key]: e.target.value
                                    })}
                                  />
                                ) : (
                                  <span className="bp-cell-text">
                                    {row.row_data?.[col.key] || '-'}
                                  </span>
                                )}
                              </td>
                            ))}
                            <td className="bp-col-confidence">
                              <ConfidenceBadge level={row.confidence} />
                            </td>
                            <td className="bp-col-status">
                              <StatusBadge status={row.status} />
                            </td>
                            <td className="bp-col-actions">
                              <div className="bp-row-actions">
                                {row.is_archived ? (
                                  <button 
                                    className="bp-icon-btn bp-restore"
                                    onClick={() => restoreRow(row.id)}
                                    title="Restore"
                                  >
                                    <Undo2 size={14} />
                                  </button>
                                ) : editingRow === row.id ? (
                                  <>
                                    <button 
                                      className="bp-icon-btn bp-save"
                                      onClick={() => updateRow(row.id, { row_data: editRowData })}
                                      title="Save"
                                    >
                                      <Save size={14} />
                                    </button>
                                    <button 
                                      className="bp-icon-btn"
                                      onClick={() => { setEditingRow(null); setEditRowData({}); }}
                                      title="Cancel"
                                    >
                                      <X size={14} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button 
                                      className="bp-icon-btn"
                                      onClick={() => {
                                        setEditingRow(row.id);
                                        setEditRowData({ ...row.row_data });
                                      }}
                                      title="Edit"
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button 
                                      className="bp-icon-btn"
                                      onClick={() => updateRow(row.id, { 
                                        status: row.status === 'published' ? 'draft' : 'published' 
                                      })}
                                      title={row.status === 'published' ? 'Unpublish' : 'Publish'}
                                    >
                                      {row.status === 'published' 
                                        ? <ToggleRight size={14} className="bp-toggle-on" /> 
                                        : <ToggleLeft size={14} />
                                      }
                                    </button>
                                    <button 
                                      className="bp-icon-btn bp-archive"
                                      onClick={() => deleteRow(row.id, false)}
                                      title="Archive"
                                    >
                                      <Archive size={14} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Row Count Footer */}
                <div className="bp-table-footer">
                  <span>
                    Showing {filteredRows.length} of {tableRows.length} rows
                    {selectedRows.size > 0 && ` (${selectedRows.size} selected)`}
                  </span>
                </div>
              </div>
            ) : (
              <div className="bp-no-table-selected">
                <Table size={48} />
                <h3>Select a Table</h3>
                <p>Choose a table from the left sidebar to manage its rows</p>
              </div>
            )}
          </div>
        )}

        {/* SECTIONS TAB */}
        {activeTab === 'sections' && (
          <div className="bp-sections">
            <div className="bp-section-grid">
              {sections.map(section => (
                <div key={section.id} className="bp-section-card" data-testid={`section-${section.section_key}`}>
                  <div className="bp-section-header">
                    <StatusBadge status={section.status} />
                    <span className="bp-section-type">{section.section_type}</span>
                  </div>
                  <h4>{section.title}</h4>
                  <p className="bp-section-desc">{section.description}</p>
                  <div className="bp-section-meta">
                    <span>Order: {section.sort_order}</span>
                    <span>Key: {section.section_key}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LISTS TAB */}
        {activeTab === 'lists' && (
          <div className="bp-lists">
            {lists.map(list => (
              <div key={list.id} className="bp-list-card" data-testid={`list-${list.section_key}`}>
                <div className="bp-list-header">
                  <div>
                    <h4>{list.title}</h4>
                    <span className="bp-list-type">{list.list_type}</span>
                  </div>
                  <StatusBadge status={list.status} />
                </div>
                <p className="bp-list-desc">{list.description}</p>
                <div className="bp-list-items">
                  {list.items?.slice(0, 5).map((item, idx) => (
                    <div key={item.id || idx} className="bp-list-item-preview">
                      <span className="bp-list-num">{idx + 1}</span>
                      <span>{item.text}</span>
                    </div>
                  ))}
                  {list.items?.length > 5 && (
                    <span className="bp-list-more">+{list.items.length - 5} more items</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSV Import Modal */}
      {showCSVModal && csvPreview && (
        <div className="bp-modal-overlay" data-testid="csv-modal">
          <div className="bp-modal bp-csv-modal">
            <div className="bp-modal-header">
              <h3><FileUp size={20} /> CSV Import Preview</h3>
              <button className="bp-modal-close" onClick={() => { setShowCSVModal(false); setCsvPreview(null); }}>
                <X size={20} />
              </button>
            </div>
            <div className="bp-modal-body">
              <div className="bp-csv-stats">
                <div className="bp-csv-stat">
                  <span className="bp-csv-stat-value">{csvPreview.total_rows}</span>
                  <span className="bp-csv-stat-label">Total Rows</span>
                </div>
                <div className="bp-csv-stat bp-csv-new">
                  <span className="bp-csv-stat-value">{csvPreview.new_rows}</span>
                  <span className="bp-csv-stat-label">New</span>
                </div>
                <div className="bp-csv-stat bp-csv-update">
                  <span className="bp-csv-stat-value">{csvPreview.update_rows}</span>
                  <span className="bp-csv-stat-label">Updates</span>
                </div>
                <div className="bp-csv-stat bp-csv-invalid">
                  <span className="bp-csv-stat-value">{csvPreview.invalid_rows}</span>
                  <span className="bp-csv-stat-label">Invalid</span>
                </div>
              </div>

              {csvPreview.duplicate_warnings?.length > 0 && (
                <div className="bp-csv-warnings">
                  <h4><AlertTriangle size={16} /> Duplicate Warnings</h4>
                  {csvPreview.duplicate_warnings.map((w, i) => (
                    <p key={i}>Row {w.row_index}: {w.message}</p>
                  ))}
                </div>
              )}

              {csvPreview.validation_errors?.length > 0 && (
                <div className="bp-csv-errors">
                  <h4><XCircle size={16} /> Validation Errors</h4>
                  {csvPreview.validation_errors.map((e, i) => (
                    <p key={i}>Row {e.row_index}: Missing fields: {e.missing_fields?.join(', ')}</p>
                  ))}
                </div>
              )}

              <div className="bp-csv-preview-table">
                <h4>Preview (first 5 rows)</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Row Key</th>
                      {Object.keys(csvPreview.preview_data?.[0]?.data || {}).slice(0, 4).map(key => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.preview_data?.slice(0, 5).map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <span className={`bp-csv-action bp-csv-action-${row.action}`}>
                            {row.action}
                          </span>
                        </td>
                        <td>{row.row_key}</td>
                        {Object.values(row.data || {}).slice(0, 4).map((val, i) => (
                          <td key={i}>{String(val).substring(0, 30)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bp-csv-import-modes">
                <h4>Import Mode</h4>
                <div className="bp-import-mode-options">
                  <button 
                    className="bp-btn bp-btn-primary"
                    onClick={() => executeCSVImport('append')}
                    data-testid="import-append"
                  >
                    <Plus size={14} /> Append Only
                    <span className="bp-btn-desc">Add new rows, skip existing</span>
                  </button>
                  <button 
                    className="bp-btn bp-btn-secondary"
                    onClick={() => executeCSVImport('update_matching')}
                    data-testid="import-update"
                  >
                    <RefreshCw size={14} /> Update Matching
                    <span className="bp-btn-desc">Update existing, add new</span>
                  </button>
                  <button 
                    className="bp-btn bp-btn-warning"
                    onClick={() => executeCSVImport('archive_missing')}
                    data-testid="import-archive"
                  >
                    <Archive size={14} /> Archive Missing
                    <span className="bp-btn-desc">Archive rows not in CSV</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionHistory && (
        <div className="bp-modal-overlay" data-testid="version-modal">
          <div className="bp-modal bp-version-modal">
            <div className="bp-modal-header">
              <h3><History size={20} /> Version History</h3>
              <button className="bp-modal-close" onClick={() => setShowVersionHistory(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="bp-modal-body">
              {versions.length === 0 ? (
                <div className="bp-empty-versions">
                  <History size={32} />
                  <p>No versions yet</p>
                </div>
              ) : (
                <div className="bp-version-list">
                  {versions.map((version, idx) => (
                    <div key={version.id} className={`bp-version-item ${idx === 0 ? 'bp-version-current' : ''}`}>
                      <div className="bp-version-info">
                        <h4>Version {version.version_number}</h4>
                        <span className="bp-version-date">
                          {new Date(version.created_at).toLocaleString()}
                        </span>
                        {version.publish_notes && (
                          <p className="bp-version-notes">{version.publish_notes}</p>
                        )}
                        <StatusBadge status={version.status} />
                      </div>
                      {idx !== 0 && (
                        <button 
                          className="bp-btn bp-btn-sm bp-btn-warning"
                          onClick={() => rollbackToVersion(version.id)}
                          data-testid={`rollback-v${version.version_number}`}
                        >
                          <RotateCcw size={14} /> Rollback
                        </button>
                      )}
                      {idx === 0 && (
                        <span className="bp-version-current-badge">Current</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAuditLog && (
        <div className="bp-modal-overlay" data-testid="audit-modal">
          <div className="bp-modal bp-audit-modal">
            <div className="bp-modal-header">
              <h3><Activity size={20} /> Audit Log</h3>
              <button className="bp-modal-close" onClick={() => setShowAuditLog(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="bp-modal-body">
              {auditLog.length === 0 ? (
                <div className="bp-empty-audit">
                  <Activity size={32} />
                  <p>No audit entries yet</p>
                </div>
              ) : (
                <div className="bp-audit-list">
                  {auditLog.map((entry) => (
                    <div key={entry.id} className="bp-audit-item">
                      <div className="bp-audit-action">
                        <span className={`bp-audit-badge bp-audit-${entry.action}`}>
                          {entry.action}
                        </span>
                      </div>
                      <div className="bp-audit-info">
                        <span className="bp-audit-entity">{entry.entity_type}</span>
                        <span className="bp-audit-by">by {entry.changed_by}</span>
                      </div>
                      <span className="bp-audit-time">
                        {new Date(entry.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlueprintAdmin;
